import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CreditCard, AlertCircle, CheckCircle2, Plus, X, Users } from "lucide-react";
import { ClassSession } from "@/data/classes";
import { StripeEmbeddedCheckout } from "@/components/StripeEmbeddedCheckout";
import { BringAFriendNotice } from "@/components/BringAFriendNotice";

import { getStripeEnvironment } from "@/lib/stripe";
import { resolveCheckoutError } from "@/lib/checkoutErrors";
import { MIN_ENROLLMENT_POLICY_TEXT } from "@/lib/enrollmentPolicy";
import {
  billingUnitLabel,
  billingUnitNoun,
  type BillingUnit,
} from "@/lib/classPricing";

interface ClassRegistrationFormProps {
  classId: string;
  className: string;
  price: string;
  unitPrice: number;
  sessions?: ClassSession[];
  schedule?: string;
  allowChildAgeZero?: boolean;
  billingUnit?: BillingUnit;
}

const BAND_LINK = "https://band.us/n/a4a5b1b2v91ar";

interface ChildEntry {
  key: string;
  firstName: string;
  lastName: string;
  age: string;
  grade: string;
  notes: string;
  selectedSessions: string[]; // session labels — for classes without sessions this stays empty
}

const emptyChild = (): ChildEntry => ({
  key: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  firstName: "",
  lastName: "",
  age: "",
  grade: "",
  notes: "",
  selectedSessions: [],
});

const ClassRegistrationForm = ({
  classId,
  className,
  price,
  unitPrice,
  sessions,
  schedule,
  allowChildAgeZero = false,
  billingUnit = "per_session",
}: ClassRegistrationFormProps) => {
  const unitNoun = billingUnitNoun(billingUnit);
  const unitLabel = billingUnitLabel(billingUnit);
  const [loading, setLoading] = useState(false);
  const [registrationError, setRegistrationError] = useState<string | null>(null);
  const [capacityLoading, setCapacityLoading] = useState(true);
  const [spotsRemaining, setSpotsRemaining] = useState<number | null>(null);
  const [capacity, setCapacity] = useState<number>(10);
  const [isFull, setIsFull] = useState(false);
  const [sessionRemaining, setSessionRemaining] = useState<Record<string, number>>({});
  const [promoCode, setPromoCode] = useState("");
  // Bring a Friend. The preview below is display-only — checkout recalculates
  // the discount server-side and is the only authority on what is charged.
  const [referralCode, setReferralCode] = useState("");
  const [referralChildIndex, setReferralChildIndex] = useState(0);
  const [referralChecking, setReferralChecking] = useState(false);
  const [referralPreview, setReferralPreview] = useState<
    { valid: boolean; message?: string; discountCents?: number } | null
  >(null);
  // Promo code state — validity is decided by the server only.
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoResult, setPromoResult] = useState<
    { valid: boolean; message: string; discountPercent?: number } | null
  >(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [accountCreditAppliedCents, setAccountCreditAppliedCents] = useState(0);
  const [checkoutTotalCents, setCheckoutTotalCents] = useState<number | null>(null);
  const [accountCreditConfirmed, setAccountCreditConfirmed] = useState(false);
  const [freeConfirmed, setFreeConfirmed] = useState(false);
  const [parent, setParent] = useState({ adult_name: "", email: "" });
  const [children, setChildren] = useState<ChildEntry[]>([emptyChild()]);

  useEffect(() => {
    setPromoResult(null);
  }, [children, parent.email, classId]);

  const isFreeClass = unitPrice === 0 || price?.toUpperCase?.() === "FREE";
  const hasSessions = !!sessions && sessions.length > 0;
  const minimumChildAge = allowChildAgeZero ? 0 : 1;
  const hasPerSessionPricing = hasSessions && sessions!.some((s) => typeof s.price === "number");

  const priceForSession = (label: string): number => {
    if (!hasSessions) return unitPrice;
    const s = sessions!.find((x) => x.label === label);
    return typeof s?.price === "number" ? s.price : unitPrice;
  };

  const totalChildSessionUnits = children.reduce(
    (sum, c) => sum + (hasSessions ? c.selectedSessions.length : 1),
    0,
  );

  const perChildTotal = (c: ChildEntry): number => {
    if (!hasSessions) return unitPrice;
    return c.selectedSessions.reduce((s, label) => s + priceForSession(label), 0);
  };

  const grandTotal = children.reduce((sum, c) => sum + perChildTotal(c), 0);

  const promoFree = promoResult?.valid === true && (promoResult.discountPercent ?? 0) >= 100;

  const checkPromoCode = async () => {
    const code = promoCode.trim();
    if (!code) {
      setPromoResult(null);
      return;
    }
    if (!parent.email.trim()) {
      setPromoResult({
        valid: false,
        message: "Enter your email address first so we can check this code.",
      });
      return;
    }
    setPromoChecking(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-class-checkout", {
        body: {
          classId,
          className,
          adultName: parent.adult_name.trim() || "Promo check",
          email: parent.email.trim(),
          promoCode: code,
          promoValidateOnly: true,
          children: children.map((child) => ({
            firstName: child.firstName,
            sessions: child.selectedSessions,
          })),
          selectedSessions: [...new Set(children.flatMap((child) => child.selectedSessions))],
        },
      });
      if (error) {
        setPromoResult({ valid: false, message: await resolveCheckoutError(error) });
        return;
      }
      if (data?.error || !data?.promoValid) {
        setPromoResult({
          valid: false,
          message:
            data?.error ??
            "That promo code isn't valid for this registration. Please check the code and try again.",
        });
        return;
      }
      setPromoResult({
        valid: true,
        discountPercent: Number(data.discountPercent) || 0,
        message: "Promo code accepted.",
      });
    } catch (err) {
      setPromoResult({ valid: false, message: await resolveCheckoutError(err) });
    } finally {
      setPromoChecking(false);
    }
  };

  // A session closed to new registration is never purchasable — existing
  // families keep their seats, but nobody new can select it.
  const isSessionClosed = (session: ClassSession) => session.closed === true;

  const isSessionUnavailable = (session: ClassSession) => {
    const remaining = sessionRemaining[session.label];
    return (
      isSessionClosed(session) ||
      session.full === true ||
      (!capacityLoading && typeof remaining === "number" && remaining <= 0)
    );
  };

  useEffect(() => {
    const checkCapacity = async () => {
      try {
        const { data, error } = await supabase.functions.invoke("check-class-capacity", {
          body: { classId },
        });
        if (!error && data) {
          setSpotsRemaining(data.spotsRemaining);
          setIsFull(data.isFull);
          if (typeof data.capacity === "number") setCapacity(data.capacity);
          if (data.sessionRemaining && typeof data.sessionRemaining === "object") {
            setSessionRemaining(data.sessionRemaining);
          }
        }
      } catch (err) {
        console.error("Failed to check capacity:", err);
      }
      setCapacityLoading(false);
    };
    checkCapacity();
  }, [classId]);

  // Preselect the soonest available session (sessions arrive ordered
  // soonest-first from withPublicSessions) so parents default to the session
  // that is running or starting next rather than a later semester.
  useEffect(() => {
    if (!hasSessions) return;
    const available = sessions!.filter((s) => !isSessionUnavailable(s));
    if (available.length === 0) return;
    setChildren((prev) =>
      prev.map((c) =>
        c.selectedSessions.length === 0 ? { ...c, selectedSessions: [available[0].label] } : c,
      ),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [capacityLoading, hasSessions, sessionRemaining]);

  const updateChild = (key: string, patch: Partial<ChildEntry>) =>
    setChildren((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));

  const addChild = () => setChildren((prev) => [...prev, emptyChild()]);

  const removeChild = (key: string) =>
    setChildren((prev) => (prev.length <= 1 ? prev : prev.filter((c) => c.key !== key)));

  const toggleChildSession = (childKey: string, label: string) =>
    setChildren((prev) =>
      prev.map((c) =>
        c.key !== childKey
          ? c
          : {
              ...c,
              selectedSessions: c.selectedSessions.includes(label)
                ? c.selectedSessions.filter((l) => l !== label)
                : [...c.selectedSessions, label],
            },
      ),
    );

  const applySessionsToAll = () => {
    if (!hasSessions) return;
    const source = children[0].selectedSessions;
    if (source.length === 0) {
      toast.error("Pick sessions for the first child first, then apply to all.");
      return;
    }
    setChildren((prev) => prev.map((c) => ({ ...c, selectedSessions: [...source] })));
    toast.success("Applied the first child's sessions to everyone.");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (clientSecret || accountCreditConfirmed) return;
    setRegistrationError(null);

    if (isFull && !hasSessions) {
      toast.error("This class is full. No more spots available.");
      return;
    }

    if (!parent.adult_name.trim() || !parent.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }

    // Validate each child.
    for (let i = 0; i < children.length; i++) {
      const c = children[i];
      const label = `Child ${i + 1}`;
      if (isFreeClass) {
        // First name still required so we can attribute the seat.
        if (!c.firstName.trim()) {
          toast.error(`${label}: please enter a first name.`);
          return;
        }
        if (c.age.trim() !== "") {
          const parsed = parseInt(c.age, 10);
          if (isNaN(parsed) || parsed < 0 || parsed > 18) {
            toast.error(`${label}: age must be 0–18 or blank.`);
            return;
          }
        }
      } else {
        const parsed = parseInt(c.age, 10);
        if (!c.firstName.trim() || isNaN(parsed) || parsed < minimumChildAge || parsed > 18) {
          toast.error(`${label}: first name and a valid age are required.`);
          return;
        }
      }
      if (hasSessions && c.selectedSessions.length === 0) {
        toast.error(`${label}: please pick at least one ${unitNoun}.`);
        return;
      }
    }

    // Duplicate warning within a single submission.
    const seen = new Set<string>();
    for (const c of children) {
      const sig = `${c.firstName.trim().toLowerCase()}|${c.lastName.trim().toLowerCase()}|${c.age.trim()}`;
      if (seen.has(sig) && c.firstName.trim()) {
        toast.error("Two child entries look identical. Please double-check names and ages.");
        return;
      }
      seen.add(sig);
    }

    // Per-session capacity check across all children.
    if (hasSessions) {
      const perLabel: Record<string, number> = {};
      for (const c of children) for (const l of c.selectedSessions) perLabel[l] = (perLabel[l] ?? 0) + 1;
      for (const [label, needed] of Object.entries(perLabel)) {
        const rem = sessionRemaining[label];
        if (typeof rem === "number" && rem < needed) {
          toast.error(
            `${label} only has ${rem} seat${rem === 1 ? "" : "s"} left, but ${needed} ${
              needed === 1 ? "child needs" : "children need"
            } to join. Please adjust.`,
          );
          return;
        }
      }
    } else if (spotsRemaining !== null && spotsRemaining < children.length) {
      toast.error(
        `Only ${spotsRemaining} seat${spotsRemaining === 1 ? "" : "s"} left — please reduce the number of children.`,
      );
      return;
    }

    setLoading(true);
    try {
      const sessionsInfo = hasSessions
        ? sessions!
            .filter((s) => children.some((c) => c.selectedSessions.includes(s.label)))
            .map((s) => ({ label: s.label, dates: s.dates }))
        : undefined;

      // Union of all sessions across children — used for the parent payment header.
      const unionSessions = hasSessions
        ? Array.from(new Set(children.flatMap((c) => c.selectedSessions)))
        : undefined;

      const childrenPayload = children.map((c) => ({
        firstName: c.firstName.trim(),
        lastName: c.lastName.trim() || null,
        age: c.age.trim() === "" ? null : parseInt(c.age, 10),
        grade: c.grade.trim() || null,
        notes: c.notes.trim() || null,
        sessions: hasSessions ? c.selectedSessions : [],
        amount: perChildTotal(c),
      }));

      const firstChild = children[0];

      const { data, error } = await supabase.functions.invoke("create-class-checkout", {
        body: {
          classId,
          className,
          adultName: parent.adult_name.trim(),
          email: parent.email.trim(),
          // Legacy compatibility fields — parent header row keeps a summary.
          childName:
            children.length === 1
              ? firstChild.firstName.trim()
              : children.map((c) => c.firstName.trim()).filter(Boolean).join(", "),
          childAge: firstChild.age.trim() === "" ? undefined : parseInt(firstChild.age, 10),
          children: childrenPayload,
          childCount: children.length,
          quantity: totalChildSessionUnits || children.length,
          selectedSessions: unionSessions,
          sessionsInfo,
          schedule,
          promoCode: promoCode.trim() || undefined,
          referralCode: referralCode.trim() || undefined,
          referralChildIndex: referralCode.trim() ? referralChildIndex : undefined,
          isFree: isFreeClass || undefined,
          environment: isFreeClass ? undefined : getStripeEnvironment(),
        },
      });

      if (error) {
        console.error("Checkout error:", error);
        setRegistrationError(await resolveCheckoutError(error));
        return;
      }
      if (data?.error) {
        // Promo failures surface inline on the field, not just as a toast.
        if (promoCode.trim()) {
          setPromoResult({ valid: false, message: data.error });
        }
        setRegistrationError(data.error);
        return;
      }
      setAccountCreditAppliedCents(Number(data?.accountCreditAppliedCents) || 0);
      setCheckoutTotalCents(typeof data?.expectedTotalCents === "number" ? data.expectedTotalCents : null);
      if (data?.accountCreditCovered) {
        setAccountCreditConfirmed(true);
        return;
      }
      if (data?.free) {
        setFreeConfirmed(true);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
      } else {
        throw new Error("No checkout session returned");
      }
    } catch (err) {
      console.error("Checkout error:", err);
      setRegistrationError(await resolveCheckoutError(err));
    } finally {
      setLoading(false);
    }
  };

  if (freeConfirmed) {
    return (
      <div className="mt-6 rounded-lg border border-border bg-card p-5 space-y-3">
        <div className="flex items-start gap-3">
          <CheckCircle2 className="text-green-600 shrink-0 mt-0.5" size={22} />
          <div className="space-y-2">
            <h4 className="font-heading text-lg font-bold text-foreground">
              You're registered for {className}!
            </h4>
            <p className="text-sm text-muted-foreground">
              There is no cost for this club. Be sure to join the Hub BAND group for ride locations,
              weather updates, and announcements.
            </p>
            <Button asChild className="font-body font-bold">
              <a href={BAND_LINK} target="_blank" rel="noopener noreferrer">
                Join the BAND group
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="mt-6 rounded-lg border border-border bg-card p-5 space-y-5">
      <fieldset disabled={!!clientSecret || accountCreditConfirmed} className="min-w-0 space-y-5">
      <h4 className="font-heading text-lg font-bold text-foreground flex items-center gap-2">
        <CreditCard size={20} className="text-primary" />
        {isFreeClass ? "Register" : "Register & Pay"}
      </h4>

      {!capacityLoading && !hasSessions && spotsRemaining !== null && (
        <div className={`flex items-center gap-2 rounded-md p-3 text-sm font-medium ${
          isFull
            ? "bg-red-50 text-red-700 border border-red-200"
            : spotsRemaining <= 3
              ? "bg-yellow-50 text-yellow-700 border border-yellow-200"
              : "bg-green-50 text-green-700 border border-green-200"
        }`}>
          <AlertCircle size={16} />
          {isFull
            ? "This class is full — no spots remaining."
            : `${spotsRemaining} spot${spotsRemaining !== 1 ? "s" : ""} remaining out of ${capacity}`}
        </div>
      )}

      {!hasSessions && isFull ? (
        <p className="text-sm text-muted-foreground">
          This class has reached its maximum capacity of {capacity} students. Please check back later or contact us for more information.
        </p>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {isFreeClass ? (
              <>Fill out the form below to register for <strong>{className}</strong>. This club is free to join.</>
            ) : (
              <>
                Fill out the form below to register for <strong>{className}</strong>.
                {hasSessions
                  ? ` Each child can choose their own ${unitNoun}(s).`
                  : ` Cost: ${price} per child.`}
              </>
            )}
          </p>

          {/* Parent info — captured once. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="reg_adult_name">Adult's Name</Label>
              <Input
                id="reg_adult_name"
                required
                maxLength={100}
                value={parent.adult_name}
                onChange={(e) => setParent({ ...parent, adult_name: e.target.value })}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="reg_email">Email Address</Label>
              <Input
                id="reg_email"
                type="email"
                required
                maxLength={255}
                value={parent.email}
                onChange={(e) => setParent({ ...parent, email: e.target.value })}
              />
            </div>
          </div>

          {/* Apply-to-all shortcut. */}
          {hasSessions && children.length > 1 && (
            <div className="flex items-center justify-between rounded-md bg-muted/50 border border-border px-3 py-2 text-sm">
              <span className="text-muted-foreground flex items-center gap-1.5">
                <Users size={14} />
                Siblings can attend the same or different {unitNoun}s.
              </span>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={applySessionsToAll}
                className="text-xs h-7"
              >
                Apply child 1's {unitNoun}s to all
              </Button>
            </div>
          )}

          {/* Repeating child sections. */}
          <div className="space-y-4">
            {children.map((child, idx) => (
              <div
                key={child.key}
                className="rounded-lg border border-border bg-background/60 p-4 space-y-3"
              >
                <div className="flex items-center justify-between">
                  <div className="font-heading font-semibold text-foreground">
                    Child {idx + 1}
                    {!hasSessions && perChildTotal(child) > 0 && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        · ${perChildTotal(child)}
                      </span>
                    )}
                    {hasSessions && child.selectedSessions.length > 0 && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        · ${perChildTotal(child)}
                      </span>
                    )}
                  </div>
                  {children.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeChild(child.key)}
                      className="text-destructive hover:text-destructive h-7 px-2"
                    >
                      <X size={14} className="mr-1" /> Remove
                    </Button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor={`child_first_${child.key}`}>
                      First Name{isFreeClass && <span className="text-muted-foreground font-normal"> (required)</span>}
                    </Label>
                    <Input
                      id={`child_first_${child.key}`}
                      required
                      maxLength={100}
                      value={child.firstName}
                      onChange={(e) => updateChild(child.key, { firstName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`child_last_${child.key}`}>
                      Last Name <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id={`child_last_${child.key}`}
                      maxLength={100}
                      value={child.lastName}
                      onChange={(e) => updateChild(child.key, { lastName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`child_age_${child.key}`}>
                      Age{isFreeClass && <span className="text-muted-foreground font-normal"> (optional)</span>}
                    </Label>
                    <Input
                      id={`child_age_${child.key}`}
                      type="number"
                      required={!isFreeClass}
                      min={minimumChildAge}
                      max={18}
                      value={child.age}
                      onChange={(e) => updateChild(child.key, { age: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor={`child_grade_${child.key}`}>
                      Grade <span className="text-muted-foreground font-normal">(optional)</span>
                    </Label>
                    <Input
                      id={`child_grade_${child.key}`}
                      maxLength={30}
                      value={child.grade}
                      onChange={(e) => updateChild(child.key, { grade: e.target.value })}
                    />
                  </div>
                </div>

                {hasSessions && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">
                      {hasPerSessionPricing
                        ? `Choose ${unitNoun}(s) for Child ${idx + 1}`
                        : `Choose ${unitNoun}(s) for Child ${idx + 1}${isFreeClass ? "" : ` — $${unitPrice} each`}`}
                    </Label>
                    {sessions!.map((session) => {
                      const sessionPrice = session.price ?? unitPrice;
                      const remaining = sessionRemaining[session.label];
                      const isSessionFull = isSessionUnavailable(session);
                      const checked = child.selectedSessions.includes(session.label);
                      return (
                        <label
                          key={session.label}
                          className={`flex items-center gap-3 rounded-md border border-border bg-background p-2.5 transition-colors ${
                            isSessionFull ? "opacity-60 cursor-not-allowed" : "cursor-pointer hover:bg-accent/50"
                          }`}
                        >
                          <Checkbox
                            checked={checked}
                            onCheckedChange={() => !isSessionFull && toggleChildSession(child.key, session.label)}
                            disabled={isSessionFull}
                          />
                          <span className="text-sm text-foreground flex-1">
                            <strong>{session.label}</strong> — {session.dates}
                            {isSessionClosed(session) ? (
                              <span className="ml-2 text-muted-foreground font-semibold">
                                (Closed to new registration)
                              </span>
                            ) : isSessionFull ? (
                              <span className="ml-2 text-red-600 font-semibold">(Full)</span>
                            ) : typeof remaining === "number" && remaining <= 3 ? (
                              <span className="ml-2 text-yellow-700 font-semibold">
                                ({remaining} left)
                              </span>
                            ) : null}
                          </span>
                          {!isFreeClass && (
                            <span className="text-sm font-semibold text-primary whitespace-nowrap">
                              ${sessionPrice}
                            </span>
                          )}
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addChild}
              className="w-full font-body font-semibold border-dashed"
            >
              <Plus size={16} className="mr-1.5" /> Add Another Child
            </Button>
          </div>

          {!isFreeClass && (
            <div className="space-y-1.5">
              <Label htmlFor="reg_promo_code">Promo Code (optional)</Label>
              <div className="flex gap-2">
                <Input
                  id="reg_promo_code"
                  maxLength={50}
                  value={promoCode}
                  onChange={(e) => {
                    setPromoCode(e.target.value);
                    setPromoResult(null);
                  }}
                  placeholder="Enter code"
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={promoChecking || !promoCode.trim()}
                  onClick={checkPromoCode}
                >
                  {promoChecking ? "Checking…" : "Apply"}
                </Button>
              </div>
              {promoResult && (
                <p className={`text-xs ${promoResult.valid ? "text-primary" : "text-destructive"}`}>
                  {promoResult.valid
                    ? promoFree
                      ? "Promo accepted — 100% discount. Final total $0.00. Free registration, no payment required."
                      : promoResult.message
                    : promoResult.message}
                </p>
              )}
            </div>
          )}

          {!isFreeClass && (
            <BringAFriendNotice
              classId={classId}
              sessionLabels={hasSessions ? sessions!.map((s) => s.label) : undefined}
            />
          )}

          {!isFreeClass && (
            <div className="space-y-1.5">
              <Label htmlFor="reg_referral_code">Have a referral code? (optional)</Label>

              <div className="flex gap-2">
                <Input
                  id="reg_referral_code"
                  maxLength={60}
                  value={referralCode}
                  onChange={(e) => {
                    setReferralCode(e.target.value.toUpperCase());
                    setReferralPreview(null);
                  }}
                  placeholder="FRIENDB-SSHH"
                />
                <Button
                  type="button"
                  variant="secondary"
                  disabled={referralChecking || !referralCode.trim()}
                  onClick={async () => {
                    const child = children[referralChildIndex] ?? children[0];
                    const label = hasSessions ? child?.selectedSessions?.[0] : "";
                    setReferralChecking(true);
                    try {
                      const { data } = await supabase.functions.invoke("validate-referral-code", {
                        body: {
                          code: referralCode.trim(),
                          classId,
                          sessionLabel: label || "",
                          email: parent.email.trim(),
                          listPriceCents: Math.round(perChildTotal(child) * 100),
                        },
                      });
                      setReferralPreview(data ?? { valid: false, message: "Please try again." });
                    } catch {
                      setReferralPreview({ valid: false, message: "Please try again." });
                    } finally {
                      setReferralChecking(false);
                    }
                  }}
                >
                  {referralChecking ? "Checking…" : "Apply"}
                </Button>
              </div>
              {children.length > 1 && referralCode.trim() && (
                <div className="text-xs text-muted-foreground">
                  Applies to:{" "}
                  <select
                    className="rounded border border-border bg-background px-1 py-0.5 text-xs"
                    value={referralChildIndex}
                    onChange={(e) => {
                      setReferralChildIndex(Number(e.target.value));
                      setReferralPreview(null);
                    }}
                  >
                    {children.map((c, i) => (
                      <option key={c.key} value={i}>
                        {c.firstName || `Child ${i + 1}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {referralPreview && (
                <p
                  className={`text-xs ${
                    referralPreview.valid ? "text-primary" : "text-destructive"
                  }`}
                >
                  {referralPreview.valid
                    ? `Referral applied — $${((referralPreview.discountCents ?? 0) / 100).toFixed(2)} off. Your final total is confirmed at checkout.`
                    : referralPreview.message}
                </p>
              )}
            </div>
          )}

          {/* Summary. */}
          {!isFreeClass && grandTotal > 0 && (
            <div className="rounded-lg border border-border bg-muted/40 p-3 space-y-1.5">
              <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Summary
              </div>
              {children.map((c, i) => (
                <div key={c.key} className="flex justify-between text-sm">
                  <span className="text-foreground">
                    {c.firstName || `Child ${i + 1}`}
                    {hasSessions && c.selectedSessions.length > 0 && (
                      <span className="text-muted-foreground"> · {c.selectedSessions.join(", ")}</span>
                    )}
                  </span>
                  <span className="font-medium">${perChildTotal(c)}</span>
                </div>
              ))}
              {referralPreview?.valid && (referralPreview.discountCents ?? 0) > 0 && (
                <div className="flex justify-between text-sm text-primary">
                  <span>Bring a Friend discount</span>
                  <span>−${((referralPreview.discountCents ?? 0) / 100).toFixed(2)}</span>
                </div>
              )}
              {accountCreditAppliedCents > 0 && (
                <div className="flex flex-wrap justify-between gap-2 text-sm text-primary">
                  <span>Account credit applied:</span>
                  <span>−${(accountCreditAppliedCents / 100).toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between border-t border-border pt-1.5 mt-1.5 font-bold text-foreground">
                <span>Total</span>
                <span>
                  $
                  {checkoutTotalCents !== null ? (checkoutTotalCents / 100).toFixed(2) : Math.max(
                    0,
                    grandTotal - (referralPreview?.valid ? (referralPreview.discountCents ?? 0) / 100 : 0),
                  ).toFixed(2)}
                </span>
              </div>
            </div>
          )}


          {registrationError && (
            <div role="alert" className="rounded-md border border-destructive bg-background p-3 text-sm text-destructive">
              <p className="font-semibold">{registrationError.includes('already registered') ? 'Already registered' : registrationError.includes('already been started') ? 'Registration already started' : 'Please review your registration'}</p>
              <p>{registrationError}</p>
            </div>
          )}
          <Button
            type="submit"
            disabled={loading || !!clientSecret || accountCreditConfirmed || (hasSessions && children.some((c) => c.selectedSessions.length === 0))}
            className={`w-full font-body font-bold ${clientSecret || accountCreditConfirmed ? "" : "sticky bottom-2"}`}
          >
            {accountCreditConfirmed
              ? "Registered — paid with account credit"
              : loading
              ? "Processing…"
              : hasSessions && children.some((c) => c.selectedSessions.length === 0)
                ? `Select a ${unitNoun} for each child`
                : isFreeClass
                  ? `Register ${children.length > 1 ? `${children.length} children` : ""}`.trim()
                  : promoFree
                    ? "Register (Free)"
                    : `Pay $${checkoutTotalCents !== null ? (checkoutTotalCents / 100).toFixed(2) : grandTotal} & Register`}
          </Button>
          {!isFreeClass && (
            <p className="text-xs text-muted-foreground text-center">
              {accountCreditConfirmed
                ? "You're registered. Your account credit covered the full tuition; no card payment is required."
                : promoFree
                ? "Free registration with promo code — no payment required."
                : clientSecret
                  ? "Complete payment below to finalize your registration."
                  : "Secure payment powered by Stripe."}
            </p>
          )}

          {/* Minimum-enrollment contingency — paid registrations only. */}
          {!isFreeClass && (
            <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              {MIN_ENROLLMENT_POLICY_TEXT}
            </p>
          )}

        </>
      )}
      </fieldset>
      {clientSecret && <StripeEmbeddedCheckout clientSecret={clientSecret} />}
    </form>
  );
};

export default ClassRegistrationForm;
